import { useEffect, useRef, useState, type FormEvent } from 'react';
import { usePageTitle } from '../lib/usePageTitle';
import { ApiError, publicApi } from '../lib/apiClient';
import { emailError, textError, urlError } from '../lib/publicFormValidation';
import './screeners.css';

// LinkedIn profile/company URL validator
function isLinkedInUrl(value: string): boolean {
  try {
    const trimmed = value.trim();

    if (!trimmed) return false;

    const withProtocol = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

    const parsed = new URL(withProtocol);

    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');

    // Only allow the official LinkedIn domain
    if (host !== 'linkedin.com') {
      return false;
    }

    // Only allow valid LinkedIn profile/company URL paths
    const pathname = parsed.pathname.replace(/\/+$/, '');

    return (
      /^\/in\/[a-zA-Z0-9._-]+$/i.test(pathname) ||
      /^\/company\/[a-zA-Z0-9._-]+$/i.test(pathname)
    );
  } catch {
    return false;
  }
}

function ScreenerApplicationModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!submittingRef.current) onClose();
      }

      if (event.key !== 'Tab') return;

      const controls = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input, select, textarea, [tabindex="0"]',
        ) ?? [],
      );

      const first = controls[0];
      const last = controls[controls.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !controls.includes(document.activeElement as HTMLElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !controls.includes(document.activeElement as HTMLElement))
      ) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      previousFocus?.focus();
    };
  }, [onClose]);

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (submittingRef.current) return;

    setSubmitError(null);

    const form = event.currentTarget;
    let firstInvalid: HTMLElement | null = null;

    form.querySelectorAll<HTMLElement>('[data-field]').forEach((field) => {
      const control = field.querySelector<HTMLInputElement | HTMLSelectElement>(
        'input, select',
      )!;

      const value = control.value.trim();

      const validationError =
        field.dataset.field === 'scdomains'
          ? field.querySelector('input:checked')
            ? undefined
            : 'Pick at least one domain.'
          : field.dataset.field === 'scname' && value && !/^[A-Za-z]+(?:[ .'-][A-Za-z]+)*$/.test(value)
            ? 'Name should contain only alphabets.'
            : control.type === 'email'
              ? emailError(value, 255)
              : control.type === 'url'
                ? urlError(value, 500) ||
                  (field.dataset.field === 'sclinkedin' &&
                  value &&
                  !isLinkedInUrl(value)
                    ? 'Enter a valid LinkedIn profile URL (e.g. linkedin.com/in/yourname).'
                    : undefined)
                : textError(
                    value,
                    field.dataset.field === 'scname'
                      ? 'Name'
                      : field.dataset.field === 'scrole'
                        ? 'Current role'
                        : 'Experience range',
                    field.dataset.field === 'scyears' ? 1 : 2,
                    field.dataset.field === 'scname'
                      ? 100
                      : field.dataset.field === 'scrole'
                        ? 255
                        : 50,
                  );

      const valid = !validationError;

      field.classList.toggle('invalid', !valid);

      const error = field.querySelector<HTMLElement>('.form-error')!;

      if (validationError) {
        error.textContent = validationError;
      }

      error.id = control.id ? control.id + '-error' : 'screenerDomains-error';

      control.setAttribute('aria-invalid', String(!valid));
      control.setAttribute('aria-describedby', error.id);

      if (!valid && !firstInvalid) {
        firstInvalid = control;
      }
    });

    if (firstInvalid) {
      (firstInvalid as HTMLElement).focus();
      return;
    }

    const value = (id: string) =>
      form.querySelector<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >(`#${id}`)!.value.trim();

    const body = {
      name: value('screenerName'),
      email: value('screenerEmail'),
      linkedinUrl: value('screenerLinkedin'),
      currentRole: value('screenerRole'),
      yearsInDomain: value('screenerYears'),
      screeningDomains: Array.from(
        form.querySelectorAll<HTMLInputElement>(
          'input[name="scdomain"]:checked',
        ),
        (input) => input.value,
      ),
      hoursPerWeek: value('screenerHours'),
      timeZone: value('screenerTz'),
      notes: value('screenerNote'),
    };

    const optionalError =
      textError(body.hoursPerWeek, 'Hours per week', 0, 20) ||
      textError(body.timeZone, 'Time zone', 0, 100) ||
      textError(body.notes, 'Notes', 0, 10000);

    if (optionalError) {
      setSubmitError(optionalError);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    try {
      await publicApi.submitScreener(body);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof ApiError
          ? error.message
          : 'Could not send your application. Please try again.',
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-overlay open"
      onClick={(event) => {
        if (
          event.target === event.currentTarget &&
          !submittingRef.current
        ) {
          onClose();
        }
      }}
      id="screenerModalOverlay"
    >
      <div
        className="modal-card"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="screenerModalTitle"
      >
        <button
          type="button"
          className="modal-close"
          id="screenerModalClose"
          disabled={submitting}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <span className="modal-eyebrow">// For screeners</span>

        <h3 id="screenerModalTitle">
          Apply to screen for QuantumHire
        </h3>

        <div className="modal-sub">
          Takes about five minutes. Preview the application below; submission
          is not connected yet.
        </div>

        {!submitted && (
          <form id="screenerForm" noValidate onSubmit={handleSubmit}>
            <div className="field-grid">
              <div className="field-row">
                <div className="field" data-field="scname">
                  <label htmlFor="screenerName">Full name</label>

                  <input
                    id="screenerName"
                    type="text"
                    placeholder="Priya Menon"
                    required
                  />

                  <span className="form-error">
                    Enter your name.
                  </span>
                </div>

                <div className="field" data-field="scemail">
                  <label htmlFor="screenerEmail">Email</label>

                  <input
                    id="screenerEmail"
                    type="email"
                    placeholder="priya@email.com"
                    required
                  />

                  <span className="form-error">
                    Enter a valid email.
                  </span>
                </div>
              </div>

              <div className="field" data-field="sclinkedin">
                <label htmlFor="screenerLinkedin">
                  LinkedIn URL
                </label>

                <input
                  id="screenerLinkedin"
                  type="url"
                  placeholder="linkedin.com/in/yourname"
                  required
                />

                <span className="form-error">
                  Enter your LinkedIn profile URL.
                </span>
              </div>

              <div className="field-row">
                <div className="field" data-field="scrole">
                  <label htmlFor="screenerRole">
                    Current role and company
                  </label>

                  <input
                    id="screenerRole"
                    type="text"
                    placeholder="Staff Engineer, Acme"
                    required
                  />

                  <span className="form-error">
                    Tell us your current role.
                  </span>
                </div>

                <div className="field" data-field="scyears">
                  <label htmlFor="screenerYears">
                    Years in your domain
                  </label>

                  <select id="screenerYears" required>
                    <option value="">Select</option>
                    <option>5–7</option>
                    <option>8–12</option>
                    <option>13–18</option>
                    <option>18+</option>
                  </select>

                  <span className="form-error">
                    Select your experience range.
                  </span>
                </div>
              </div>

              <div className="field" data-field="scdomains">
                <label id="screenerDomainsLabel">
                  Domains you can screen for
                </label>

                <div
                  className="scr-chips"
                  role="group"
                  aria-labelledby="screenerDomainsLabel"
                >
                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Backend"
                    />
                    Backend
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Frontend"
                    />
                    Frontend
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Data/ML"
                    />
                    Data &amp; ML
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="DevOps"
                    />
                    DevOps &amp; platform
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Product"
                    />
                    Product
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Eng Mgmt"
                    />
                    Eng management
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Finance"
                    />
                    Finance
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="GTM"
                    />
                    Sales &amp; GTM
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="Design"
                    />
                    Design
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="QA"
                    />
                    QA &amp; SDET
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="scdomain"
                      value="HR"
                    />
                    HR &amp; people
                  </label>
                </div>

                <span className="form-error">
                  Pick at least one domain.
                </span>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="screenerHours">
                    Hours per week
                  </label>

                  <select id="screenerHours">
                    <option>2–3</option>
                    <option>4–6</option>
                    <option>7+</option>
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="screenerTz">
                    Time zone
                  </label>

                  <select id="screenerTz">
                    <option>IST (India)</option>
                    <option>US Eastern</option>
                    <option>US Pacific</option>
                    <option>ICT (Thailand)</option>
                    <option>Canada Eastern</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="screenerNote">
                  Anything we should know?{' '}
                  <span className="opt">
                    (employer exclusions, roles you've hired for,
                    interviewing experience)
                  </span>
                </label>

                <textarea
                  id="screenerNote"
                  placeholder="e.g. I've run 100+ backend interviews at my current company. Please exclude candidates going to Acme or its competitors."
                ></textarea>
              </div>

              {submitError && (
                <p
                  className="form-error"
                  role="alert"
                  style={{ display: 'block' }}
                >
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                className="form-submit"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? 'Sending…' : 'Send application →'}
              </button>

              <div className="form-note">
                If it's a fit, the next step is a 45-minute calibration
                screen.
              </div>
            </div>
          </form>
        )}

        {submitted && (
          <div
            className="form-success show"
            id="screenerFormSuccess"
            role="status"
            tabIndex={-1}
            ref={successRef}
          >
            <div className="check">✓</div>

            <h3>Application received.</h3>

            <p>
              If it's a fit, we'll be in touch to arrange a calibration
              screen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function ScreenersPage() {
  usePageTitle('For Screeners — QuantumHire');

  const [modalOpen, setModalOpen] = useState(false);
  const pageRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        }),
      { threshold: 0.1 },
    );

    pageRef.current
      ?.querySelectorAll('.reveal')
      .forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="screeners-page">
      <main ref={pageRef} inert={modalOpen}>
        <header className="prog-hero">
          <div className="prog-hero-inner">
            <div>
              <div className="prog-eyebrow">
                <span className="dot-live"></span> For screeners
              </div>

              <h1>
                Be the technical screen behind <em>every</em>{' '}
                QuantumHire shortlist.
              </h1>

              <p className="lede">
                No candidate reaches a QuantumHire client without a
                technical screen by someone who actually does the job. If
                you're a senior practitioner with a spare hour a week,
                we'll send you pre-matched candidates, handle everything
                around the call, and pay you per screen.
              </p>

              <div className="prog-hero-cta">
                <button
                  type="button"
                  className="prog-btn-primary"
                  onClick={(event) => {
                    event.preventDefault();
                    setModalOpen(true);
                  }}
                >
                  Apply to screen
                </button>

                <a
                  href="#scr-how"
                  className="prog-btn-ghost"
                  onClick={(event) => {
                    event.preventDefault();

                    document
                      .getElementById('scr-how')
                      ?.scrollIntoView({
                        behavior: window.matchMedia(
                          '(prefers-reduced-motion: reduce)',
                        ).matches
                          ? 'auto'
                          : 'smooth',
                      });
                  }}
                >
                  See how it works ↓
                </a>
              </div>

              <p className="scr-hero-note">
                Selective by design. Every screener clears a calibration
                round before their first live screen. Your verdict decides
                whether a candidate goes to the client.
              </p>
            </div>

            <div
              className="scr-brief"
              aria-label="Example screening request"
            >
              <div className="scr-brief-top">
                <span>Tech screen · pre-submission</span>
                <b>QH-2417</b>
              </div>

              <h3>Senior Backend Engineer</h3>

              <div className="scr-co">
                Series B fintech, Bengaluru (hybrid)
              </div>

              <dl>
                <dt>Format</dt>
                <dd>45 min technical screen, recorded</dd>

                <dt>Candidate</dt>
                <dd>9 yrs · Go, Postgres, Kafka</dd>

                <dt>Already cleared</dt>
                <dd>QuantumMatch + recruiter call</dd>

                <dt>Your slot</dt>
                <dd>Tue 7:30 PM IST</dd>
              </dl>

              <div className="scr-card">
                <h4>Scorecard you'll submit</h4>

                <div className="scr-row">
                  <span>System design depth</span>

                  <div className="scr-dots">
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                    <i></i>
                  </div>
                </div>

                <div className="scr-row">
                  <span>Production ownership</span>

                  <div className="scr-dots">
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                    <i></i>
                    <i></i>
                  </div>
                </div>

                <div className="scr-row">
                  <span>Communication</span>

                  <div className="scr-dots">
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                    <i className="on"></i>
                  </div>
                </div>

                <div className="scr-row">
                  <span>Verdict</span>
                  <b>Clear for submission</b>
                </div>
              </div>

              <div className="scr-fee">
                <span>Paid on scorecard submission</span>
                <b>Fee per screen</b>
              </div>
            </div>
          </div>
        </header>

        <section className="prog-section" id="scr-how">
          <div className="prog-section-head">
            <h2>How it works</h2>

            <p>
              Four steps from application to your first paid screen. Most
              screeners are live within two weeks.
            </p>
          </div>

          <div className="scr-steps">
            <div className="scr-step reveal">
              <h3>Apply</h3>

              <p>
                Tell us your domain, seniority and the hours you can give.
                We review every application by hand.
              </p>

              <small>5 minutes</small>
            </div>

            <div className="scr-step reveal">
              <h3>Calibrate</h3>

              <p>
                Run one mock screen with a QuantumHire lead so we agree on
                the rubric and what "clear" means.
              </p>

              <small>45 minutes, once</small>
            </div>

            <div className="scr-step reveal">
              <h3>Get matched</h3>

              <p>
                When a recruiter shortlists a candidate in your domain, you
                get the JD, the candidate's profile and open slots.
              </p>

              <small>Accept or pass, no penalty</small>
            </div>

            <div className="scr-step reveal">
              <h3>Screen and score</h3>

              <p>
                Run the call on our link and submit the scorecard. Clear
                means the candidate goes to the client; hold or reject means
                they don't.
              </p>

              <small>Paid monthly</small>
            </div>
          </div>
        </section>

        <section className="prog-section tint">
          <div className="prog-section-head">
            <h2>You screen. We do everything else.</h2>

            <p>
              No sourcing, no scheduling ping-pong, no client contact.
              Screens are internal to QuantumHire; the only thing we ask for
              is the hour and your honest read.
            </p>
          </div>

          <div className="scr-split">
            <div className="scr-panel you reveal">
              <h3>What you do</h3>

              <ul>
                <li>
                  Accept requests that fit your calendar and domain
                </li>
                <li>
                  Run a structured 45-minute technical screen from the rubric
                </li>
                <li>
                  Submit a scorecard with a clear / hold / reject verdict
                </li>
                <li>
                  Flag anything the recruiter should double-check
                </li>
              </ul>
            </div>

            <div className="scr-panel we reveal">
              <h3>What we handle</h3>

              <ul>
                <li>
                  Matching and shortlisting every candidate before they
                  reach you
                </li>
                <li>
                  Scheduling, reminders, the video link and the recording
                </li>
                <li>
                  All candidate and client communication, before and after
                  your screen
                </li>
                <li>
                  Contracts, invoicing and monthly payouts
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="prog-section scr-dark">
          <div className="prog-section-head">
            <h2>Who we're looking for</h2>

            <p>
              Working practitioners, not full-time interviewers. Highlighted
              areas are where we submit the most candidates right now.
            </p>
          </div>

          <div className="scr-domains">
            <span className="scr-domain hot">
              Backend engineering
            </span>

            <span className="scr-domain hot">
              Data &amp; ML engineering
            </span>

            <span className="scr-domain hot">
              Product management
            </span>

            <span className="scr-domain">
              Frontend engineering
            </span>

            <span className="scr-domain">
              DevOps &amp; platform
            </span>

            <span className="scr-domain">
              Engineering management
            </span>

            <span className="scr-domain hot">
              Finance &amp; controllership
            </span>

            <span className="scr-domain">
              Sales &amp; GTM leadership
            </span>

            <span className="scr-domain">Design</span>

            <span className="scr-domain">QA &amp; SDET</span>

            <span className="scr-domain">
              HR &amp; people leadership
            </span>
          </div>

          <div className="scr-reqs">
            <div className="scr-req reveal">
              <h3>8+ years in the domain</h3>

              <p>
                You've hired for the role, been the role, or managed the
                role — ideally all three.
              </p>
            </div>

            <div className="scr-req reveal">
              <h3>Still doing the work</h3>

              <p>
                Currently employed, founding, or consulting in the field. We
                don't onboard career interviewers.
              </p>
            </div>

            <div className="scr-req reveal">
              <h3>2 to 6 hours a week</h3>

              <p>
                Evenings and weekends are fine. You set availability and can
                pause it any time.
              </p>
            </div>
          </div>
        </section>

        <section className="prog-section">
          <div className="scr-loop">
            <div>
              <h2>
                Your verdict is the gate, and it makes the next search
                smarter
              </h2>

              <p>
                Nothing QuantumHire submits to a client skips the technical
                screen. That means the client only ever meets candidates a
                practitioner has already cleared, and your scorecard summary
                travels with the shortlist.
              </p>

              <p>
                It also feeds QuantumMatch
                <span className="tm-inline">™</span>, our matching engine,
                which learns from what recruiters and screeners report back.
                Your reads on system design depth or product judgment shape
                which profiles surface next time we hire for that role or
                that client.
              </p>
            </div>

            <div className="scr-loop-diagram reveal">
              <ol>
                <li>
                  <div className="n">JD</div>

                  <div>
                    <b>Client shares a req</b>
                    <span>
                      QuantumMatch ranks profiles from 5M+ CVs
                    </span>
                  </div>
                </li>

                <li>
                  <div className="n">R</div>

                  <div>
                    <b>Recruiter shortlists</b>
                    <span>
                      Calls candidates, feeds context back
                    </span>
                  </div>
                </li>

                <li className="you">
                  <div className="n">You</div>

                  <div>
                    <b>Internal technical screen</b>
                    <span>
                      Every candidate passes this gate before submission
                    </span>
                  </div>
                </li>

                <li>
                  <div className="n">C</div>

                  <div>
                    <b>Client receives shortlist</b>
                    <span>
                      Only cleared candidates, with a screen summary
                      attached
                    </span>
                  </div>
                </li>

                <li>
                  <div className="n">QM</div>

                  <div>
                    <b>QuantumMatch learns</b>
                    <span>
                      Scorecards tune the next search for that role
                    </span>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <section className="prog-section tint">
          <div className="prog-section-head">
            <h2>Questions screeners ask us</h2>
          </div>

          <div className="scr-faq">
            <details>
              <summary>How much does a screen pay?</summary>

              <p>
                A fixed fee per completed screen, set by domain and
                seniority of the role. You see the fee on every request
                before you accept. Fees are paid out monthly against a simple
                invoice; we handle the paperwork.
              </p>
            </details>

            <details>
              <summary>
                Is there a conflict with my current employer?
              </summary>

              <p>
                You'll never be sent candidates for your own company or its
                direct competitors. Tell us your employer and any exclusions
                at application and we'll enforce them. Many employers treat
                this as external interviewing experience; check your own
                policy first.
              </p>
            </details>

            <details>
              <summary>
                Do clients see my name or talk to me?
              </summary>

              <p>
                No. Screens are internal to QuantumHire. Clients receive a
                summary of the scorecard with the shortlist, but you're
                never named to them and never asked to join client calls.
              </p>
            </details>

            <details>
              <summary>
                Do I need to source or evaluate CVs?
              </summary>

              <p>
                No. Every candidate has already been matched by QuantumMatch
                and shortlisted by a QuantumHire recruiter. You get one
                candidate, one JD and one rubric per request.
              </p>
            </details>

            <details>
              <summary>What if a candidate doesn't show up?</summary>

              <p>
                We pay a no-show fee for confirmed slots where the candidate
                doesn't join within 10 minutes. We also reschedule on our
                side; you don't chase anyone.
              </p>
            </details>

            <details>
              <summary>Can I screen from outside India?</summary>

              <p>
                Yes. We hire for clients in India, the US, Thailand and
                Canada and need screeners across those time zones. Payouts
                work internationally.
              </p>
            </details>

            <details>
              <summary>What is the calibration round?</summary>

              <p>
                One 45-minute mock screen with a QuantumHire lead where you
                run our rubric on a sample candidate. It's how we make sure
                "clear" means the same thing from every screener. It's paid
                like a normal screen.
              </p>
            </details>
          </div>
        </section>

        <section className="prog-cta-strip">
          <h2>
            Have a spare hour a week and strong opinions about who's good?
          </h2>

          <p>
            Applications are reviewed weekly. We'll reply either way.
          </p>

          <div className="prog-hero-cta">
            <button
              type="button"
              className="prog-btn-primary"
              onClick={(event) => {
                event.preventDefault();
                setModalOpen(true);
              }}
            >
              Apply to screen
            </button>
          </div>
        </section>
      </main>

      {modalOpen && (
        <ScreenerApplicationModal
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}